<template>
    <v-product-line-layout :group="group" :collapseAllVariations="collapseAllVariations" @remove="variationDeleted" @updateSortIndex="updateSortIndex" :showSortIndex="true" ref="productLayout">
        <slot slot="title">{{group.ErpId}} - {{group.Name}}</slot>
        <slot slot="product">
            <v-product-delete @change="removeProductGroup" title="product.thumb.delete"></v-product-delete>
            <v-gallery :images="Images"></v-gallery>
            <div class="c-product-colors">
                <strong class="u-semi-bold">{{ $t('product.thumb.colors') }}:</strong>
                <ul class="c-product-colors__list">
                    <li class="u-font-small" v-for="color in group.Colors" :key="color">
                        <a href="#" @click="scrollToColor($event, color)">{{ color }}</a>
                    </li>
                </ul>
            </div>
            <div class="c-product-right u-font-small">
                <div>
                    <div class="c-product-info-parent">
                        <table class="c-product-info">
                            <tr>
                                <td><strong class="u-semi-bold">{{ $t('product.thumb.brand') }}:</strong></td>
                                <td>{{ group.Brand }}</td>
                            </tr>
                            <tr>
                                <td><strong class="u-semi-bold">{{ $t('product.thumb.category') }}:</strong></td>
                                <td>{{ group.BrandSubgroup }}</td>
                            </tr>
                            <tr v-if="group.Type">
                                <td><strong class="u-semi-bold">{{ $t('product.thumb.segment') }}:</strong></td>
                                <td>{{ group.Type }}</td>
                            </tr>
                        </table>

                        <table class="c-product-info">
                            <tr>
                                <td><strong class="u-semi-bold">{{ $t('product.thumb.brochure') }}:</strong></td>
                                <td>
                                    <v-input v-model.number="group.Brochure"
                                             name="brochure"
                                             type="number"
                                             width="50px"
                                             :min=0
                                             input-size="tiny"
                                             @input="updateProductGroup">
                                    </v-input>
                                </td>
                            </tr>
                            <tr v-if="group.Packaging">
                                <td><strong class="u-semi-bold">{{ $t('product.thumb.packaging') }}:</strong></td>
                                <td>{{ group.packaging }}</td>
                            </tr>
                            <tr>
                                <td><label class="u-semi-bold">{{ $t('product.thumb.preOrder_discount') }}:</label></td>
                                <td>
                                    <v-input v-model.number="group.MaximumDiscount"
                                             @change="discountToChildren($event.target.value)"
                                             name="max_discount" type="number" :max=100 width="50px" :min=0 input-size="tiny">
                                    </v-input>
                                </td>
                            </tr>
                        </table>
                    </div>
                </div>
                <div class="c-product-summary">
                    <v-input v-model="group.Note" name="note" :fluid="true" @input="updateProductGroup">
                        <slot slot="label">Opmerking</slot>
                    </v-input>
                </div>
            </div>
        </slot>
        <slot slot="variations">
            <v-product-variation-line :parent-id="parentId" :discount-value="discountValue"
                                        v-for="product in group.Products" 
                                        :product="product" 
                                        :key="product.Id" 
                                        :group-index="groupIndex">
            </v-product-variation-line>
        </slot>
    </v-product-line-layout>
</template>

<script>
    import debounce from 'lodash/debounce'
    import ScrollTo from 'vue-scrollto'
    import VGallery from '@/components/gallery/Gallery'
    import VSelect from '@/components/form/Select'
    import VInput from '@/components/form/Input'
    import VProductVariationLine from '@/components/product/admin/ProductVariationLine'
    import VSwitch from '@/components/form/Switch'
    import VProductDelete from '@/components/product/widgets/productDelete'
    import VProductLineLayout from '@/components/product/layout/ProductLineLayout'

    export default {
        props: {
            group: Object,
            parentId: String,
            groupIndex: Number,
            collapseAllVariations: Boolean
        },
        components: {
            VGallery,
            VSelect,
            VInput,
            VSwitch,
            VProductVariationLine,
            VProductDelete,
            VProductLineLayout,
        },
        data() {
            return {
                discountValue: null
            }
        },
        computed: {
            interactiveValues() {
                return {
                    Brochure: this.group.Brochure,
                    MaximumDiscount: this.group.MaximumDiscount,
                    Note: this.group.Note
                }
            },
            Images() {
                let output = !this.group.Images.length ? null : [];

                this.group.Images.forEach((image, index) => {
                    if (index < 6) {
                        output.push({
                            src: image.replace('[WIDTH]', 230).replace('[HEIGHT]', 180),
                            selected: index === 0
                        })
                    }
                });

                return output
            }
        },
        methods: {
            updateProductGroup: debounce(function () {
                let params = this.interactiveValues;
                this.$store.dispatch('updateGroupToSheet', {
                    preOrderSheetId: this.parentId,
                    productGroupId: this.group.Id,
                    body: {
                        Id: this.group.Id,
                        ...params
                    }
                });
            }, 500),
            updateSortIndex: debounce(function() {
                this.$store.dispatch('updateGroupSortIndexToSheet', {
                    preOrderSheetId: this.parentId,
                    productGroupId: this.group.Id,
                    sortIndex: this.group.SortIndex
                }).then(() => {
                    this.$emit('sortIndexUpdated')
                });
            }, 500),
            discountToChildren: debounce(function (value) {
                this.updateProductGroup();
                this.discountValue = parseInt(value);
            }, 500),
            removeProductGroup() {
                this.$store.dispatch('deleteProductGroupFromSheet', {
                    preOrderSheetId: this.parentId,
                    productGroupId: this.group.Id
                }).then(() => {
                    this.$emit('deleted');
                });
            },
            variationDeleted() {
                if(this.group.Products.length <= 1) {
                    this.$emit('deleted');
                }
            },
            getScrollToAnchor(color) {
                let product = this.group.Products.find(product => {
                    return (product.ColorId + ' ' + product.Color) === color;
                });

                if(product) {
                    return '#variant_' + product.Id;
                }
            },
            scrollToColor(e, color){
                e.preventDefault();
                this.$refs.productLayout.collapseAllVariations = false;
                const anchor = this.getScrollToAnchor(color);

                setTimeout(function() {
                    ScrollTo.scrollTo(anchor, 300, {offset: -55});
                }, 1);
            }
        }
    }
</script>

<style lang="scss" scoped>

    @import "~@/assets/scss/private";

</style>



// WEBPACK FOOTER //
// src/components/product/admin/ProductLine.vue